import WatsonApi from "../backends/WatsonApi";

export type IMinimalOrganization = Pick<OrganizationTypeOrganization, "id" | "title" | "path"> & {
    requires_request: boolean;
    is_primary: boolean;
    preventDeselect?: boolean;
}

export const processContentSourceSharingOrganizations = (
    selectedOrgs: IMinimalOrganization[],
    currentOrganization: OrganizationTypeOrganization
): { selectedOrganizations: IMinimalOrganization[] } => {
    const selectedOrganizations: IMinimalOrganization[] = [];

    const currentOrg: IMinimalOrganization = {
        id: currentOrganization.id,
        title: currentOrganization.title,
        path: currentOrganization.path,
        is_primary: true,
        requires_request: false,
    };

    selectedOrganizations.push(currentOrg);

    selectedOrgs.forEach(org => {
        if (org.id !== currentOrganization.id) {
            selectedOrganizations.push({
                ...org,
                is_primary: false,
            });
        }
    });

    return { selectedOrganizations };
};


export const updateContentSourceSharing = async (
    sourceId: string | number,
    selectedOrganizations: IMinimalOrganization[],
    contentSource: ContentSource,
    currentOrganization: OrganizationTypeOrganization,
    mutate?: () => Promise<any>
): Promise<ContentSource> => {
    const client = await WatsonApi();

    // Current organization is the primary owner, only send shared organizations to backend
    const contentSourceShares = selectedOrganizations
        .filter(org => org.id !== currentOrganization.id)
        .map(org => ({
            organization_id: org.id,
            share_source: currentOrganization.id,
        }));

    const response = await client.apis.organizations.organizations_content_sources_partial_update({
        organization_pk: currentOrganization.id,
        id: sourceId,
        data: {
            source_url: contentSource.source_url,
            content_source_shares: contentSourceShares,
        },
    });

    const updatedSource = JSON.parse(response.data);

    // Refresh cache if mutate function provided
    if (mutate) {
        await mutate();
    }

    return updatedSource;
};

export type ValidatedContentItemOrganizations = {
    selectedOrganizations: IMinimalOrganization[];
    shareRequestOrganizations: IMinimalOrganization[];
    primaryOrganizationId: string | undefined;
};

export const validateContentItemSharingOrganizations = async (
    organizationId: string,
    contentItemId: string | number | undefined,
    selectedOrgIds: string[]
): Promise<ValidatedContentItemOrganizations> => {
    const client = await WatsonApi();
    const response = await client.apis["content-items"].content_items_validate_share_org_ids({
        organization_id: organizationId,
        content_item_id: contentItemId,
        share_org_ids: selectedOrgIds,
    });

    const result: Array<{
        title: string;
        id: string;
        path: string;
        requires_request: boolean;
        is_primary: boolean;
    }> = JSON.parse(response.data);

    // Split organizations based on permission requirements
    const selectedOrgs: IMinimalOrganization[] = [];
    const requestOrgs: IMinimalOrganization[] = [];

    result.forEach(org => {
        if (org.requires_request) {
            requestOrgs.push(org);
        } else {
            selectedOrgs.push(org);
        }
    });

    const primaryOrgId = result.find(o => o.is_primary)?.id;

    return {
        selectedOrganizations: selectedOrgs,
        shareRequestOrganizations: requestOrgs,
        primaryOrganizationId: primaryOrgId,
    };
};

export const filterOrganizationsForBackend = (
    selectedOrganizations: IMinimalOrganization[]
): IMinimalOrganization[] => {
    return selectedOrganizations.filter(org => !org.is_primary);
};


export const formatContentItemSharesForBackend = (
    selectedOrganizations: IMinimalOrganization[],
    shareRequestOrganizations: IMinimalOrganization[]
): Array<{ organization_id: string }> => {
    return [
        ...selectedOrganizations.map(o => ({ organization_id: o.id })),
        ...shareRequestOrganizations.map(o => ({ organization_id: o.id })),
    ];
};

export const formatContentSourceSharesForBackend = (
    selectedOrganizations: IMinimalOrganization[],
    currentOrganizationId: string
): Array<{ organization_id: string; share_source: string }> => {
    return selectedOrganizations
        .filter(org => org.id !== currentOrganizationId)
        .map(org => ({
            organization_id: org.id,
            share_source: currentOrganizationId,
        }));
};

export const initializeSharingOrganizations = (
    ownerOrganization: OrganizationTypeOrganization,
    shareOrganizations?: OrganizationTypeOrganization[]
): IMinimalOrganization[] => {
    if (!shareOrganizations || shareOrganizations.length === 0) {
        return [];
    }

    return shareOrganizations.map(org => {
        const isPrimary = org.id === ownerOrganization.id;
        return {
            id: org.id,
            title: org.title || '',
            path: org.path || '',
            is_primary: isPrimary,
            requires_request: false,
            preventDeselect: isPrimary,
        };
    });
};